import { Gift, X } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import CartaoFidelidade from "./CartaoFidelidade";
import { vencimento } from "./premios";

/** "🎁 Você tem um prêmio" — a faixa no topo da sacola e do cardápio. */
export function FaixaPremio({ premios, onUsar, onVer, darkMode = false }) {
  if (!premios?.length) return null;
  const p = premios[0];
  return (
    <div className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border ${darkMode ? "bg-amber-400/10 border-amber-400/30" : "bg-amber-50 border-amber-200"}`}
      data-e2e="faixa-premio">
      <span className="text-2xl leading-none">🎁</span>
      <button type="button" onClick={onVer} className="min-w-0 flex-1 text-left">
        <span className={`block text-sm font-bold ${darkMode ? "text-amber-200" : "text-amber-900"}`}>Você tem um prêmio!</span>
        <span className={`block text-xs truncate ${darkMode ? "text-amber-100/80" : "text-amber-800"}`}>
          {p.texto}{p.vence_em ? ` · vale até ${vencimento(p.vence_em)}` : ""}{premios.length > 1 ? ` · +${premios.length - 1}` : ""}
        </span>
      </button>
      <button type="button" onClick={() => onUsar(p)} data-e2e="usar-premio"
        className="shrink-0 h-9 px-3 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600">Usar</button>
    </div>
  );
}

/** A tira do cardápio: os 10 espaços em miniatura e quanto falta. Toque abre "Meu cartão". */
export function TiraCartao({ estado, onAbrir, darkMode = false }) {
  const c = estado?.cartao;
  if (!c?.participa) return null;
  const tema = estado.loja?.tema || {};
  return (
    <button type="button" onClick={onAbrir} data-e2e="tira-cartao"
      className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-white shadow-sm active:scale-[0.99] transition-transform"
      style={{ background: `linear-gradient(135deg, ${tema.c1}, ${tema.c2})` }}>
      <span className="text-xl leading-none">{tema.emoji}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-bold">
          Cartão fidelidade · {c.status === "aguardando" ? "completo!" : `${c.faltam === 1 ? "falta" : "faltam"} ${c.faltam} para ${c.premio?.texto || "o prêmio"}`}
        </span>
        <span className="mt-1 flex gap-1" aria-hidden>
          {Array.from({ length: c.total }, (_, i) => (
            <i key={i} className="h-1.5 flex-1 rounded-full" style={{ background: i < c.carimbos ? tema.accent : "rgba(255,255,255,.25)" }} />
          ))}
        </span>
      </span>
      <b className="text-sm tabular-nums shrink-0" style={{ color: tema.accent }}>{c.carimbos}/{c.total}</b>
    </button>
  );
}

/** "Meu cartão": o cartão animado, os prêmios para usar e como funciona. */
export function MeuCartaoModal({ aberto, onFechar, estado, onUsar, darkMode = false }) {
  if (!estado) return null;
  const c = estado.cartao || {};
  const premios = estado.premios || [];
  const txt = darkMode ? "text-white" : "text-gray-900";
  const sub = darkMode ? "text-white/70" : "text-gray-600";
  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent hideClose className={`p-0 gap-0 overflow-hidden border-0 sm:max-w-lg w-[94vw] max-h-[90vh] rounded-2xl flex flex-col ${darkMode ? "bg-zinc-900" : "bg-white"}`}>
        <DialogHeader className="sr-only"><DialogTitle>Meu cartão fidelidade</DialogTitle></DialogHeader>
        <div className="flex items-center justify-between px-4 pt-4">
          <h2 className={`text-lg font-bold ${txt}`}>Meu cartão fidelidade</h2>
          <button type="button" onClick={onFechar} aria-label="Fechar" className={`w-9 h-9 grid place-items-center rounded-full ${darkMode ? "hover:bg-white/10 text-white" : "hover:bg-gray-100"}`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-4 space-y-4">
          {c.participa ? (
            <>
              <CartaoFidelidade loja={estado.loja} cartao={c} />
              <p className={`text-sm text-center ${sub}`}>
                {c.status === "aguardando"
                  ? <>Cartão completo! O prêmio fica liberado assim que o seu último pedido for concluído.</>
                  : <>Faltam <b className={txt}>{c.faltam}</b> {c.faltam === 1 ? "compra" : "compras"} para ganhar <b className={txt}>{c.premio?.texto}</b>.</>}
                {c.valido_ate && c.status === "aberto" && <> Compre até {vencimento(c.valido_ate + "T12:00:00")} para os carimbos não zerarem.</>}
              </p>
            </>
          ) : (
            <p className={`text-sm text-center ${sub}`}>O cartão fidelidade desta loja não está disponível para você agora.</p>
          )}

          {premios.length > 0 && (
            <div className="space-y-2">
              <p className={`text-sm font-bold ${txt}`}>Prêmios para usar</p>
              {premios.map((p) => (
                <div key={p.codigo} className={`flex items-center gap-3 rounded-xl p-3 border ${darkMode ? "border-amber-400/30 bg-amber-400/10" : "border-amber-200 bg-amber-50"}`}>
                  <Gift className="w-5 h-5 text-amber-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-bold ${txt}`}>{p.texto}</p>
                    <p className={`text-xs ${sub}`}>Vale até {vencimento(p.vence_em)}{(p.tipo === "produto" || p.tipo === "pizza") ? " · coloque o produto na sacola" : ""}</p>
                  </div>
                  <button type="button" onClick={() => onUsar(p)} className="shrink-0 h-9 px-3 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600">
                    Usar
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className={`text-xs space-y-1 ${sub}`}>
            <p>• Cada compra vale um carimbo. Os 2 primeiros são presente da loja.</p>
            <p>• No 10º carimbo o prêmio fica guardado aqui, sem código para digitar.</p>
            <p>• O prêmio entra no fechamento do pedido e não soma com outro cupom.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
